import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { environment } from '@environments/environment';
import {
  GoogleAuthProvider,
  User as FirebaseUser,
  createUserWithEmailAndPassword,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { getFirebaseAuth } from './firebase';

/** The OTA guest, mapped from the guest-genie Firebase user. */
export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
}

export interface RegisterProfile {
  firstName: string;
  lastName: string;
}

/**
 * Guest authentication, backed by the guest-genie Firebase project.
 *
 * OTA guests ARE guest-genie users: registering here triggers guest-genie's
 * Firebase `onCreate` to provision the canonical Mongo `User`, so no extra call
 * is needed. The Firebase ID token (kept fresh via `onIdTokenChanged`) is sent
 * to the property-manager backend, which introspects it against guest-genie.
 *
 * The synchronous `token()` signal mirrors the latest ID token for the HTTP
 * interceptor; Firebase auto-refreshes it before expiry.
 */
@Injectable({ providedIn: 'root' })
export class AuthTokenService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly browser = isPlatformBrowser(this.platformId);

  private readonly _token = signal<string | null>(environment.devJwt || null);
  private readonly _user = signal<AuthUser | null>(null);
  private readonly _ready = signal(false);

  /** Latest Firebase ID token (or the legacy dev override). */
  readonly token = this._token.asReadonly();
  /** The signed-in guest, or null. */
  readonly user = this._user.asReadonly();
  /** True once the initial Firebase auth state has resolved. */
  readonly ready = this._ready.asReadonly();
  readonly isAuthenticated = computed(() => !!this._user());

  constructor() {
    if (!this.browser) {
      this._ready.set(true);
      return;
    }
    const auth = getFirebaseAuth();
    onIdTokenChanged(auth, async (user) => {
      if (user) {
        this._user.set(mapUser(user));
        this._token.set(await user.getIdToken());
      } else {
        this._user.set(null);
        this._token.set(environment.devJwt || null);
      }
      this._ready.set(true);
    });
  }

  get hasToken(): boolean {
    return !!this._token();
  }

  /** Legacy manual override; real tokens come from Firebase. */
  setToken(token: string | null): void {
    this._token.set(token);
  }

  async login(email: string, password: string): Promise<void> {
    const cred = await signInWithEmailAndPassword(
      getFirebaseAuth(),
      email,
      password,
    );
    await this.adopt(cred.user);
  }

  async register(
    email: string,
    password: string,
    profile: RegisterProfile,
  ): Promise<void> {
    const cred = await createUserWithEmailAndPassword(
      getFirebaseAuth(),
      email,
      password,
    );
    const displayName = `${profile.firstName} ${profile.lastName}`.trim();
    if (displayName) {
      await updateProfile(cred.user, { displayName });
    }
    await this.adopt(cred.user);
  }

  async loginWithGoogle(): Promise<void> {
    const cred = await signInWithPopup(
      getFirebaseAuth(),
      new GoogleAuthProvider(),
    );
    await this.adopt(cred.user);
  }

  async logout(): Promise<void> {
    await signOut(getFirebaseAuth());
    this._user.set(null);
    this._token.set(environment.devJwt || null);
  }

  /** Resolves once the initial Firebase auth state is known. */
  async whenReady(): Promise<void> {
    if (this._ready()) return;
    await new Promise<void>((resolve) => {
      const auth = getFirebaseAuth();
      const unsub = onIdTokenChanged(auth, () => {
        unsub();
        resolve();
      });
    });
  }

  /** Eagerly populate user + token so callers don't race `onIdTokenChanged`. */
  private async adopt(user: FirebaseUser): Promise<void> {
    this._user.set(mapUser(user));
    this._token.set(await user.getIdToken());
    this._ready.set(true);
  }
}

function mapUser(user: FirebaseUser): AuthUser {
  const [firstName, ...rest] = (user.displayName ?? '').trim().split(/\s+/);
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    firstName: firstName || null,
    lastName: rest.length ? rest.join(' ') : null,
    phoneNumber: user.phoneNumber,
  };
}
